/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { IconButton } from '../UNSAFE_IconButton';
import { BundleType } from '../resources/nls/bundle';
import { IncrementIcon } from '../PRIVATE_ThemedIcons/IncrementIcon';
import { DecrementIcon } from '../PRIVATE_ThemedIcons/DecrementIcon';
import { StepDirection, StepEvent } from './useSteppers';
import { TabbableModeContext } from '../hooks/UNSAFE_useTabbableMode';
import { useCallback } from 'preact/hooks';
import { useTranslationBundle } from '../hooks/UNSAFE_useTranslationBundle';

type Props = {
  /**
   * The direction of the step.
   */
  direction: StepDirection;

  /**
   * Whether the button is disabled.
   */
  isDisabled?: boolean;

  /**
   * Called when a step request is made by clicking the button.
   */
  onStep?: (event: StepEvent) => void;

  /**
   * Variants representing different icons for the button.
   */
  variant: 'arrowup' | 'arrowdown';
};

/**
 * A StepButton is used to request a "step up" or "step down" to a value in NumberInputText.
 * @param direction The direction of the step
 * @param isDisabled Whether the button should be disabled
 * @param onStep Handler called when the button is clicked
 * @param variant Represents different visual variants for the button
 */
export function StepButton({ direction, isDisabled, onStep, variant }: Props) {
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const labelIncrease = translations.inputNumber_increase();
  const labelDecrease = translations.inputNumber_decrease();
  const label = direction === 'increase' ? labelIncrease : labelDecrease;

  const handleClick = useCallback(() => {
    onStep?.({ direction });
  }, [onStep, direction]);

  const icon = variant === 'arrowup' ? <IncrementIcon /> : <DecrementIcon />;

  return (
    <TabbableModeContext.Provider value={{ isTabbable: false }}>
      <IconButton
        isDisabled={isDisabled}
        tooltip={label}
        onAction={handleClick}
        variant="borderless">
        {icon}
      </IconButton>
    </TabbableModeContext.Provider>
  );
}
